import React, { Component } from "react";
import { Row, Col, Button, Modal, Input, Select, message, List, Space, Typography } from 'antd';
import { RedoOutlined, SearchOutlined } from '@ant-design/icons';
import CustomAvatar from "../../Widgets/Avatar/Avatar";
import moment from 'moment'


//Paybook
// import SyncWidget from "@paybook/sync-widget";
// import "@paybook/sync-widget/dist/widget.css";
import SyncWidget from "@syncfy/authentication-widget";

const { Text } = Typography;
const axios = require('axios').default;

/**
 * @class ModalRenovarCuentasSyncfy
 * @description Modal para renovar credenciales de cuentas bancarias conectadas a Syncfy.
 * 
 * === PROPÓSITO ===
 * Permite a los usuarios renovar las credenciales de sus cuentas bancarias cuando éstas
 * expiran o requieren reautenticación en la plataforma Syncfy.
 * 
 * === FLUJO PRINCIPAL ===
 * 1. INICIALIZACIÓN:
 *    - Carga automáticamente todas las cuentas que tienen integración con Syncfy
 *    - Muestra lista paginada con búsqueda
 * 
 * 2. PROCESO DE RENOVACIÓN:
 *    a) Usuario hace clic en botón "Renovar" de una cuenta
 *    b) Se cierra cualquier sesión Syncfy previa (deleteSession)
 *    c) Se inicia nueva sesión con el usuario Syncfy de la cuenta (GET /syncfy/session)
 *    d) Se renderiza el widget de Syncfy para renovar credenciales (renderWidget)
 *    e) Widget maneja la autenticación con el banco
 * 
 * 3. FINALIZACIÓN:
 *    - Widget emite evento "success" o "error"
 *    - Se actualiza la cuenta en BD con nuevo estatus (update)
 *    - Se refresca la lista de cuentas
 *    - Se limpia el estado de loading
 */
class ModalRenovarCuentasSyncfy extends Component {


    constructor(props) {
        super(props);
        this.state = {
            cuentas: {
                data: [],
                limit: 20,
                page: 1,

                total: 0,
                pages: 0,
            },
            activo: undefined,
            id_site_organization_type: "56cf4f5b784806cf028b4568"
        }
    }

    componentDidMount() {
        axios.defaults.headers.common['Authorization'] = sessionStorage.getItem('token');
        this.get()
    }

    componentWillUnmount(){
        window.syncWidget = undefined
    }

    get = ({
        page = this.state.cuentas.page,
        limit = this.state.cuentas.limit,
        search = this.state.search,
    } = this.state.cuentas, {cuentas} = this.state) => {
        this.setState({cuentas: {...cuentas, page, limit}, activo: undefined, loading: true, search})
        axios.get('/cuentas', {
            params: {
                page,
                limit,
                search,
                con_syncfy: true
            }
        }).then(({data}) => {
            this.setState({
                cuentas: {...cuentas, ...data}
            })
        }).catch(error => {
            message.error("Error al obtener las cuentas")
        }).finally(() => {
            this.setState({loading: false})
        })
    }
    
    /**
     *
     *
     * @param {object} cuenta Objecto con los datos de la cuenta
     * @memberof ModalRenovarCuentasSyncfy
     * @name renovate
     * @description Realiza la renovacion de las credenciales Syncfy de una cuenta
     */
    renovate = async (cuenta) => {
        this.setState({ activo: cuenta._id });

        try {
            // Cerrar sesión previa si existe
            await this.deleteSession();

            // Iniciar sesión con el usuario de Syncfy
            const { data } = await axios.get('/syncfy/session', { params: { id_user_syncfy: cuenta.syncfy_usuario_id } });
            sessionStorage.setItem('syncfy', data.token);
            axios.defaults.headers.common['syncfy'] = data.token;

            // Renderizar el widget para renovar credenciales
            this.renderWidget(cuenta.syncfy_credenciales_id, cuenta._id);
        } catch (error) {
            console.error('Error en la renovación:', error);
            message.error("Error al renovar las credenciales");
            this.update({ response: { error }, id: cuenta._id });
            this.setState({ activo: undefined });
        }
    }

    /**
     * @name deleteSession
     * @memberof ModalRenovarCuentasSyncfy
     * @description Realiza una solicitud DELETE a la API para eliminar la sesión de Syncfy y,
     * al completarse, elimina la información de la sesión almacenada en sessionStorage.
     * @returns {Promise<void>} Una promesa que se resuelve cuando la sesión ha sido eliminada.
     */
    deleteSession = () => {
        return axios.delete('/syncfy/session')
            .then(({ data }) => {
                sessionStorage.removeItem('syncfy')
            })
            .finally(() => console.log('Sessión Eliminada'))
    }

    /**
     * @name update
     * @memberof ModalRenovarCuentasSyncfy
     * @description se activa después del proceso de renovación de credenciales Syncfy.
     * para guardar cuando se realizo la renovacion de credenciales de una cuenta.
     * Se ejecuta automáticamente cuando cambia el resultado del widget de Syncfy mediante componentDidUpdate.
     */
    update = async ({id, response} = {}) => {
        if (!id || !response) {
            console.error("ID y respuesta son requeridos para actualizar la cuenta");
            return;
        }
        try{
            await axios.put(`/cuentas`, { 
                id, 
                estatus: response?.success ? 1 : 2,
                renovacion: true,
                respuesta: response
            })
        }catch(error){
            console.log("ERROR: ", error)
            message.error("Error al actualizar las credenciales de la cuenta")
            return
        }finally{
            this.get()
            this.setState({ activo: undefined })
        }
    }

    /**
     *
     *
     * @param {string} credential Credencial de la cuenta Syncfy
     * @param {string} cuenta_id Identificador de la cuenta a renovar
     * @memberof ModalRenovarCuentasSyncfy
     * @name renderWidget
     * @description Renderiza un widget de Syncfy que realiza la actualizacion de credenciales
     */
    renderWidget = (credential, cuenta_id) => {

        try{

            const syncfyToken = sessionStorage.getItem('syncfy');
            if (!syncfyToken) {
                return Modal.error({
                    title: "Debe seleccionar un Usuario de Syncfy para poder generar las credenciales."
                });
            }
    
            const widgetConfig = {
                token: syncfyToken,
                config: {
                    locale: 'es',
                    navigation: {
                        displayStatusInToast: true,
                        displaySiteOrganizationTypes: this.state.id_site_organization_type ? [this.state.id_site_organization_type] : null
                    },
                    entrypoint: {
                        credential: credential || undefined,
                    }
                }
            };
    
            if (!window.syncWidget) {
                window.syncWidget = new SyncWidget(widgetConfig);
            } else {
                window.syncWidget.setConfig(widgetConfig.config);
                window.syncWidget.setToken(syncfyToken);
            }
    
    
            window.syncWidget.on("error", e => {
                this.update({  id: cuenta_id, response: { error: e } });
            });
            window.syncWidget.on("success", e => {
                this.update({ id: cuenta_id, response: { success: e } });
            });
    
            if (!credential) {
                window.syncWidget.open();
            }
        }catch(e){
            console.log(e);
            message.error("Hubo un error al iniciar el widget, refrescando la página.");
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        }
    }


    render() {
        const { cuentas } = this.state
        return (
            <Row gutter={[24, 24]}>
                <Col xs={24}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Buscar Cuenta"
                        value={this.state.search}
                        onChange={(e) => this.get({search: e.target.value})}     
                    />
                </Col>
                <Col xs={24}>
                    <List
                        loading={this.state.loading}
                        itemLayout="horizontal"
                        locale={{ emptyText: "Sin Cuentas" }}
                        dataSource={cuentas.data}
                        pagination={{
                            current: cuentas?.page,
                            pageSize: cuentas?.limit,
                            total: cuentas?.total,
                            position: 'bottom',
                            className: "flex-left",
                            onChange: (page, limit) => {
                                this.getCuentas({ page, limit })
                            }
                        }}
                        renderItem={item => (
                            <List.Item className="">
                                <Row className="width-100 " align={"middle"} gutter={[16, 16]}>
                                    <Col xs={2}>
                                        <CustomAvatar 
                                            name={item.nombre} 
                                            
                                            color={item.color}
                                            url={ item.logo ? {
                                                url: `${axios.defaults.baseURL}/cuentas/${item?._id}`,
                                                logo: item?.logo?.name
                                            } : null}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Space size={2} direction="vertical">
                                            <Text>{item.nombre}</Text>
                                            <Text className="text-gray">{item.cuenta}</Text>
                                        </Space>
                                    </Col>
                                    <Col xs={8}>
                                        <Space size={2} direction="vertical">
                                            <Text className="text-gray">Ultima renovación</Text>
                                            <Text>{moment(item.renovacion?.createdAt || item.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                                        </Space>
                                    </Col>
                                    <Col xs={2}>
                                        <Button
                                            type="primary"
                                            icon={<RedoOutlined />}
                                            title="Renovar"
                                            disabled={this.state.activo && this.state.activo !== item._id}
                                            loading={this.state.activo === item._id}
                                            onClick={() => this.renovate(item)}
                                        />
                                    </Col>
                                </Row>
                            </List.Item>
                        )}
                    />
                </Col>
            </Row>
        )
    }
}


export default function (props) {
    const { visible = false, onClose = () => { }, cuenta_id } = props

    return <Modal
        visible={visible}
        onCancel={onClose}
        title={"Renovar Credenciales de Syncfy"}
        destroyOnClose={true}
        footer={null}
    >
        <ModalRenovarCuentasSyncfy {...props} />
    </Modal>

}